import {
  ClosedPosition,
  OpenPosition,
  PaginationRequest,
  PaginationResult,
  Position,
  Trade,
} from '@monorepo/types';
import BaseEntityService, { BaseResponse } from '../core/base-entity-service';

export default class PositionService extends BaseEntityService {
  static override route = 'position';

  async getClosedPositions(
    tsAccountId: string,
    paginateRequest: PaginationRequest,
  ) {
    try {
      const response = await this.httpService.get<
        BaseResponse<PaginationResult<ClosedPosition>>
      >(`${this.path}/closed/${tsAccountId}`, {
        params: paginateRequest,
      });

      return response.data;
    } catch (error) {
      console.error(
        `Failed getting closed positions for account: ${tsAccountId}`,
        error,
      );
      return {
        total: 0,
        results: [],
      };
    }
  }

  async getOpenPositions(
    tsAccountId: string,
    paginateRequest?: PaginationRequest,
  ) {
    try {
      const response = await this.httpService.get<
        BaseResponse<PaginationResult<OpenPosition>>
      >(`${this.path}/open/${tsAccountId}`, {
        params: paginateRequest,
      });

      return response.data;
    } catch (error) {
      console.error(
        `Failed getting closed positions for account: ${tsAccountId}`,
        error,
      );
      return {
        total: 0,
        results: [],
      };
    }
  }

  async getAllPositions(
    tsAccountId: string,
    paginateRequest?: PaginationRequest,
  ) {
    try {
      const response = await this.httpService.get<
        BaseResponse<PaginationResult<OpenPosition | ClosedPosition>>
      >(`${this.path}/all/${tsAccountId}`, {
        params: paginateRequest,
      });

      return response.data;
    } catch (error) {
      console.error(
        `Failed getting all positions for account: ${tsAccountId}`,
        error,
      );
      return {
        total: 0,
        results: [],
      };
    }
  }

  async getValidPositionsCount(tsAccountId: string) {
    try {
      const response = await this.httpService.get<
        BaseResponse<{ valid: number }>
      >(`${this.path}/valid-positions/${tsAccountId}`);

      return response.data;
    } catch (e) {
      console.error(
        `Failed getting valid positions count for account: ${tsAccountId}`,
        e,
      );
      return {
        valid: 0,
      };
    }
  }

  async getTrades(tsAccountId: string, positionId: string) {
    try {
      const response = await this.httpService.get<
        BaseResponse<PaginationResult<Trade>>
      >(`${this.path}/${tsAccountId}/trades/${positionId}`);

      return response.data;
    } catch (e) {
      console.error(
        `Failed getting trades of position ${positionId} for account: ${tsAccountId}`,
        e,
      );
      return {
        total: 0,
        results: [],
      };
    }
  }

  async getContestOpenPositions(
    tsAccountId: string,
    paginateRequest?: PaginationRequest,
  ) {
    try {
      const response = await this.httpService.get<
        BaseResponse<PaginationResult<OpenPosition>>
      >(`${this.path}/contest-open/${tsAccountId}`, {
        params: paginateRequest,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      return response.data;
    } catch (e) {
      console.error(
        `Failed getting contest open positions for account: ${tsAccountId}`,
        e,
      );
      throw e;
    }
  }

  async getContestAllPositions(
    tsAccountId: string,
    paginateRequest?: PaginationRequest,
  ) {
    try {
      const response = await this.httpService.get<
        BaseResponse<PaginationResult<OpenPosition | ClosedPosition>>
      >(`${this.path}/contest-all/${tsAccountId}`, {
        params: paginateRequest,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      return response.data;
    } catch (e) {
      console.error(
        `Failed getting all contest positions for account: ${tsAccountId}`,
        e,
      );
      throw e;
    }
  }

  async getContestClosedPositions(
    accountId: string,
    paginateRequest: PaginationRequest,
  ) {
    try {
      const response = await this.httpService.get<
        BaseResponse<PaginationResult<ClosedPosition>>
      >(`${this.path}/contest-closed/${accountId}`, {
        params: paginateRequest,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      return response.data;
    } catch (e) {
      console.error(
        `Failed getting closed positions for account: ${accountId}`,
        e,
      );
      throw e;
    }
  }

  async getPosition(tsAccountId: string, positionId: string) {
    try {
      const response = await this.httpService.get<BaseResponse<Position>>(
        `${this.path}/${tsAccountId}/${positionId}`,
      );
      return response.data;
    } catch (e) {
      console.error(`Failed getting position for id: ${positionId}`, e);
      return null;
    }
  }

  async uploadPositionImage(positionId: string, image: string) {
    const response = await this.httpService.post<
      { id: string; image: string },
      BaseResponse<{ url: string }>
    >(`${this.path}/upload-position-image`, {
      id: `${positionId}-${Date.now()}`,
      image,
    });
    return response.data;
  }
}
